import { BLACK, type Color, KING, PAWN, QUEEN, TYPE_MASK, WHITE, colorBit, other, pieceColor } from '../board.ts';
import { WALL, pieceDef } from '../pieces.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, draw, rulesFor, win } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 4 (draft). The board shrinks. After move 10 (ply 20) the outer ring of squares turns
// into walls, destroying whatever stands there; after move 20 (ply 40) the next ring goes
// too, leaving a 4×4 arena. Walls are neutral: nothing can land on or pass through them.
// Leaving your king on a ring that collapses before your next move is illegal, like
// leaving it in check; with no way out you're caught in the storm and lose. Once the edge
// has gone, a pawn that reaches the last row still standing promotes to a queen.
//
// ext: [plies played, capped at the last storm]

const PLY = 0;
/** The ply after which each ring collapses (ring 0 = the edge). */
export const STORM_PLIES = [20, 40] as const;
const LAST = STORM_PLIES[STORM_PLIES.length - 1];

export const STORM_FEN = `${STANDARD_FEN} 0`;

/** 0 for the edge squares, 1 for the next ring in, … 3 for the centre. */
export function ringOf(sq: number): number {
  const f = sq & 7;
  const r = sq >> 3;
  return Math.min(f, 7 - f, r, 7 - r);
}

const RINGS: number[][] = [[], [], [], []];
for (let sq = 0; sq < 64; sq++) RINGS[ringOf(sq)].push(sq);

export function stormPly(pos: { ext: Int32Array | null }): number {
  return pos.ext?.[PLY] ?? 0;
}

/** How many rings have collapsed after `ply` plies. */
export function ringsGone(ply: number): number {
  let n = 0;
  while (n < STORM_PLIES.length && ply >= STORM_PLIES[n]) n++;
  return n;
}

/** The next ring to collapse and the plies until it does (1 = right after the next move), or null. */
export function nextStorm(pos: { ext: Int32Array | null }): { ring: number; in: number } | null {
  const ply = stormPly(pos);
  const ring = ringsGone(ply);
  if (ring >= STORM_PLIES.length) return null;
  return { ring, in: STORM_PLIES[ring] - ply };
}

/** The side to move must get its king off a ring that collapses before it moves again. */
export function inStormDanger(pos: Position): boolean {
  const next = nextStorm(pos);
  const k = pos.royals[pos.turn];
  return !!next && next.in <= 2 && k >= 0 && ringOf(k) <= next.ring;
}

export const storm: Variant = {
  id: 'storm',
  info: {
    name: 'Storm Chess',
    but: 'the board shrinks',
    tagline: 'Every 10 moves the edge of the board crumbles away, and anything standing there goes with it.',
    summary: 'After move 10 the **outer ring turns to wall**, destroying everything on it. After move 20 the next ring goes. Get your king to the middle.',
    rules: [
      'After move 10 the outer ring of squares becomes wall, destroying any piece on it. After move 20 the next ring goes too, leaving a 4×4 board.',
      'Nothing can land on or pass through a wall.',
      'Your king may not be on a ring when it collapses. If you can’t get it out, you’re caught in the storm and lose.',
      'Once the edge has gone, a pawn that reaches the last row still standing becomes a queen.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#2ed3b7',
    emoji: '🌪️',
    bots: ['Drizzle', 'Squall', 'Thunderhead', 'Hurricane', 'Eye of the Storm'],
  },
  startFen: STORM_FEN,
  extraPieces: [WALL],
  extSize: 1,
  // While a storm is still coming it can catch a king, so any material might win. Once the
  // arena is final, walls are just edges and the usual dead positions are dead.
  insufficientMaterial: (pos) => nextStorm(pos) === null,

  parseExtra(fields, pos) {
    const [plies = '0'] = fields;
    const n = Number(plies);
    if (!Number.isInteger(n) || n < 0) throw new FenError('Bad storm ply count');
    pos.ext![PLY] = Math.min(n, LAST);
  },

  formatExtra(pos) {
    return [String(pos.ext![PLY])];
  },

  afterMove(ctx: MoveContext) {
    const { next } = ctx;
    const ext = next.ext!;
    if (ext[PLY] < LAST) {
      ext[PLY]++;
      const ring = STORM_PLIES.indexOf(ext[PLY] as (typeof STORM_PLIES)[number]);
      if (ring >= 0) for (const sq of RINGS[ring]) ctx.place(sq, WALL, 'storm');
    }
    // Once the edge has gone, pawns on the last row still standing become queens.
    const gone = ringsGone(ext[PLY]);
    if (!gone) return;
    for (const c of [WHITE, BLACK] as Color[]) {
      const pawn = PAWN | colorBit(c);
      const queen = QUEEN | colorBit(c);
      const row = (c === WHITE ? 7 - gone : gone) * 8;
      for (let sq = row; sq < row + 8; sq++) {
        if (next.board[sq] !== pawn) continue;
        next.board[sq] = queen;
        ctx.events?.push({ t: 'add', sq, piece: queen, cause: 'promotion' });
      }
    }
  },

  isLegal(ctx: MoveContext) {
    // The opponent's next move sets off a storm: the king must already be clear of it.
    const { next, us } = ctx;
    const ply = next.ext![PLY];
    const gone = ringsGone(ply);
    const k = next.royals[us];
    return !(gone < STORM_PLIES.length && STORM_PLIES[gone] - ply === 1 && k >= 0 && ringOf(k) <= gone);
  },

  threatened: inStormDanger,

  noMoves(pos) {
    const rules = rulesFor(storm);
    if (rules.inCheck(pos)) return win(other(pos.turn), 'checkmate');
    if (inStormDanger(pos)) return win(other(pos.turn), 'storm');
    return draw('stalemate');
  },

  evaluate(pos) {
    const next = nextStorm(pos);
    if (!next || next.in > 12) return 0;
    // Pieces on the doomed ring are worth less the closer the storm gets; kings must leave.
    const weight = next.in <= 2 ? 0.6 : next.in <= 6 ? 0.3 : 0.1;
    let score = 0;
    for (const sq of RINGS[next.ring]) {
      const p = pos.board[sq];
      const c = pieceColor(p);
      if (c === -1) continue;
      const t = p & TYPE_MASK;
      const value = t === KING ? 250 + 40 * (12 - next.in) : pieceDef(t).value * weight;
      score += c === WHITE ? -value : value;
    }
    return score;
  },

  reasonText: {
    storm: 'Caught in the storm',
  },
};

